import React, { useState, useEffect } from 'react';
import saasPlatformService from '../../services/saasPlatformService';
import './SaasPlatform.css';

const KeyLifecycleManagement = () => {
  const [inventory, setInventory] = useState(null);
  const [auditHistory, setAuditHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);

  // Selected key detail / dry run state
  const [dryRunReport, setDryRunReport] = useState(null);
  const [activeTab, setActiveTab] = useState('inventory'); // 'inventory', 'dryrun', 'history'

  // Modals & form states
  const [requestModalKey, setRequestModalKey] = useState(null);
  const [requestComment, setRequestComment] = useState('');

  const [confirmModalKey, setConfirmModalKey] = useState(null);
  const [confirmToken, setConfirmToken] = useState('');
  const [confirmComment, setConfirmComment] = useState('');

  const [recoveryModalKey, setRecoveryModalKey] = useState(null);
  const [backupVerified, setBackupVerified] = useState(false);
  const [recoveryNotes, setRecoveryNotes] = useState('');

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [invRes, histRes] = await Promise.all([
        saasPlatformService.getKeyInventory(),
        saasPlatformService.getKeyHistory(50)
      ]);
      setInventory(invRes?.data || invRes);
      setAuditHistory(histRes?.data || histRes || []);
    } catch (err) {
      console.error('Failed to load key inventory', err);
      setError(err?.response?.data?.message || err?.message || 'Failed to connect to Key Lifecycle service.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleDryRun = async (version) => {
    setActionLoading(true);
    setError(null);
    setSuccessMsg(null);
    try {
      const res = await saasPlatformService.dryRunKeyRetirement(version);
      const report = res?.data || res;
      setDryRunReport(report);
      setActiveTab('dryrun');
      setSuccessMsg(`Dry-run audit for key '${version}' completed: ${report.isEligibleForRetirement ? 'ELIGIBLE' : 'BLOCKED'}`);
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || `Dry run failed for key ${version}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeprecate = async (version) => {
    if (!window.confirm(`Are you sure you want to deprecate key '${version}'? It will no longer be usable for new encryption.`)) {
      return;
    }
    setActionLoading(true);
    setError(null);
    setSuccessMsg(null);
    try {
      const res = await saasPlatformService.deprecateKey(version, 'Operator requested key deprecation');
      setSuccessMsg(res?.message || `Key '${version}' deprecated successfully.`);
      await loadData();
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || `Deprecation failed for key ${version}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleOpenRequestRetirement = (version) => {
    setRequestModalKey(version);
    setRequestComment('');
  };

  const handleSubmitRequestRetirement = async () => {
    if (!requestComment.trim()) {
      alert('An operator comment explaining the retirement request is required.');
      return;
    }
    setActionLoading(true);
    setError(null);
    setSuccessMsg(null);
    try {
      const res = await saasPlatformService.requestKeyRetirement(requestModalKey, { comment: requestComment.trim() });
      const result = res?.data || res;
      setRequestModalKey(null);
      if (result.success && result.confirmationToken) {
        setSuccessMsg(`Retirement requested for '${requestModalKey}'. Confirmation Token: ${result.confirmationToken}`);
        setConfirmModalKey(requestModalKey);
        setConfirmToken(result.confirmationToken);
      } else {
        setError(result.message || 'Retirement request blocked by server validation.');
      }
      await loadData();
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || `Request retirement failed for ${requestModalKey}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleSubmitConfirmRetirement = async () => {
    if (!confirmToken.trim() || !confirmComment.trim()) {
      alert('Confirmation token and operator comment are both required to confirm retirement.');
      return;
    }
    setActionLoading(true);
    setError(null);
    setSuccessMsg(null);
    try {
      const res = await saasPlatformService.confirmKeyRetirement(confirmModalKey, {
        confirmationToken: confirmToken.trim(),
        comment: confirmComment.trim()
      });
      const result = res?.data || res;
      setConfirmModalKey(null);
      if (result.success) {
        setSuccessMsg(`Key '${confirmModalKey}' has been safely RETIRED. External KMS removal status: ${result.externalKeyRemovalStatus}`);
      } else {
        setError(result.message || 'Key retirement confirmation failed.');
      }
      await loadData();
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || `Confirmation failed for ${confirmModalKey}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancelRetirement = async (version) => {
    const reason = window.prompt(`Enter reason for cancelling retirement of key '${version}':`);
    if (!reason) return;
    setActionLoading(true);
    setError(null);
    setSuccessMsg(null);
    try {
      const res = await saasPlatformService.cancelKeyRetirement(version, reason);
      setSuccessMsg(res?.message || `Retirement cancelled for '${version}'. Reverted to DEPRECATED.`);
      await loadData();
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || `Cancellation failed for key ${version}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleSubmitRecoveryReview = async () => {
    if (!backupVerified) {
      alert('You must attest that backups and disaster recovery copies have been verified.');
      return;
    }
    if (!recoveryNotes.trim()) {
      alert('Please enter recovery verification notes.');
      return;
    }
    setActionLoading(true);
    setError(null);
    setSuccessMsg(null);
    try {
      const res = await saasPlatformService.submitRecoveryReview(recoveryModalKey, {
        backupsVerified: backupVerified,
        notes: recoveryNotes.trim()
      });
      setRecoveryModalKey(null);
      setSuccessMsg(res?.message || `Disaster recovery review registered for '${recoveryModalKey}'.`);
      await loadData();
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || `Recovery review failed for ${recoveryModalKey}`);
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusBadge = (state) => {
    switch (state) {
      case 'ACTIVE':
        return <span className="saas-badge saas-badge-active">● ACTIVE</span>;
      case 'DECRYPT_ONLY':
        return <span className="saas-badge saas-badge-info">🔓 DECRYPT_ONLY</span>;
      case 'DEPRECATED':
        return <span className="saas-badge saas-badge-suspended">⚠️ DEPRECATED</span>;
      case 'RETIREMENT_PENDING':
        return <span className="saas-badge" style={{ backgroundColor: '#fed7aa', color: '#c2410c' }}>⏳ RETIREMENT_PENDING</span>;
      case 'RETIRED':
        return <span className="saas-badge" style={{ backgroundColor: '#f1f5f9', color: '#64748b' }}>🛑 RETIRED</span>;
      default:
        return <span className="saas-badge saas-badge-info">{state}</span>;
    }
  };

  return (
    <div className="saas-container">
      {/* Header */}
      <div className="saas-header">
        <div className="saas-header-title">
          <h1>
            <span>🔑</span> Cryptographic Key Lifecycle &amp; Retirement Audit
          </h1>
          <p>Key Inventory, Usage Auditing, Two-Phase Safe Retirement &amp; Disaster Recovery Compliance</p>
        </div>
        <div className="saas-header-actions">
          <button className="saas-btn saas-btn-secondary" onClick={loadData} disabled={loading || actionLoading}>
            🔄 Refresh Key Ring
          </button>
        </div>
      </div>

      {/* Notifications */}
      {error && (
        <div className="saas-error-box">
          <div>
            <strong>Error:</strong> {error}
          </div>
          <button className="saas-btn saas-btn-secondary" style={{ padding: '0.3rem 0.6rem' }} onClick={() => setError(null)}>
            Dismiss
          </button>
        </div>
      )}

      {successMsg && (
        <div style={{ backgroundColor: '#ecfdf5', border: '1px solid #a7f3d0', color: '#065f46', padding: '1rem', borderRadius: '8px', marginBottom: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <strong>Success:</strong> {successMsg}
          </div>
          <button className="saas-btn saas-btn-secondary" style={{ padding: '0.3rem 0.6rem' }} onClick={() => setSuccessMsg(null)}>
            Dismiss
          </button>
        </div>
      )}

      {/* Tab Navigation */}
      <div style={{ display: 'flex', gap: '0.5rem', borderBottom: '1px solid #e2e8f0', marginBottom: '1.5rem' }}>
        <button
          className={`saas-btn ${activeTab === 'inventory' ? 'saas-btn-primary' : 'saas-btn-secondary'}`}
          onClick={() => setActiveTab('inventory')}
        >
          🗝️ Key Ring Inventory ({inventory?.totalConfiguredKeys ?? 0})
        </button>
        <button
          className={`saas-btn ${activeTab === 'dryrun' ? 'saas-btn-primary' : 'saas-btn-secondary'}`}
          onClick={() => setActiveTab('dryrun')}
        >
          🔍 Retirement Dry-Run Audit {dryRunReport && `(${dryRunReport.keyVersion})`}
        </button>
        <button
          className={`saas-btn ${activeTab === 'history' ? 'saas-btn-primary' : 'saas-btn-secondary'}`}
          onClick={() => setActiveTab('history')}
        >
          📜 Audit &amp; Lifecycle History ({auditHistory.length})
        </button>
      </div>

      {loading ? (
        <div className="saas-loading">
          <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>⏳</div>
          Auditing key ring cryptographic status and scanning catalog ciphertexts...
        </div>
      ) : (
        <>
          {/* TAB 1: Key Inventory */}
          {activeTab === 'inventory' && inventory && (
            <>
              {/* Summary Metrics */}
              <div className="saas-stats-grid">
                <div className="saas-stat-card">
                  <span className="saas-stat-label">Active Encryption Key</span>
                  <span className="saas-stat-value" style={{ color: '#0284c7' }}>
                    {inventory.activeKeyVersion || 'None'}
                  </span>
                  <span className="saas-stat-subtext">Used for all new ciphertexts</span>
                </div>
                <div className="saas-stat-card">
                  <span className="saas-stat-label">Total Key Versions</span>
                  <span className="saas-stat-value">{inventory.totalConfiguredKeys}</span>
                  <span className="saas-stat-subtext">Configured in key ring</span>
                </div>
                <div className="saas-stat-card">
                  <span className="saas-stat-label">Catalog Ciphertexts</span>
                  <span className="saas-stat-value">{inventory.totalCiphertextsFound}</span>
                  <span className="saas-stat-subtext">Tenant database credentials</span>
                </div>
                <div className="saas-stat-card">
                  <span className="saas-stat-label">Unversioned Payloads</span>
                  <span className="saas-stat-value" style={{ color: inventory.totalLegacyUnversioned > 0 ? '#ea580c' : '#047857' }}>
                    {inventory.totalLegacyUnversioned}
                  </span>
                  <span className="saas-stat-subtext">Requiring legacy key migration</span>
                </div>
              </div>

              {/* Keys Inventory Table */}
              <div className="saas-card">
                <div className="saas-card-header">
                  <h3>Configured Cryptographic Keys</h3>
                  <span style={{ fontSize: '0.8125rem', color: '#64748b' }}>
                    Audited at: {new Date(inventory.auditedAtUtc).toLocaleString()}
                  </span>
                </div>
                <div className="saas-table-container">
                  <table className="saas-table">
                    <thead>
                      <tr>
                        <th>Version</th>
                        <th>Lifecycle State</th>
                        <th>Permissions</th>
                        <th>Ciphertext Count</th>
                        <th>Retirement Eligibility</th>
                        <th>Recovery Review</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {inventory.keys && inventory.keys.length > 0 ? (
                        inventory.keys.map((k) => (
                          <tr key={k.version}>
                            <td>
                              <strong style={{ fontFamily: 'monospace', fontSize: '0.95rem' }}>{k.version}</strong>
                              {k.isActiveEncryptionKey && (
                                <span style={{ marginLeft: '0.5rem', fontSize: '0.75rem', color: '#0284c7', fontWeight: 'bold' }}>
                                  (ACTIVE)
                                </span>
                              )}
                            </td>
                            <td>{getStatusBadge(k.state)}</td>
                            <td>
                              <div style={{ fontSize: '0.8125rem' }}>
                                <span style={{ color: k.canEncrypt ? '#047857' : '#94a3b8' }}>
                                  {k.canEncrypt ? '✓ Encrypt' : '✗ Encrypt'}
                                </span>
                                {' | '}
                                <span style={{ color: k.canDecrypt ? '#047857' : '#b91c1c' }}>
                                  {k.canDecrypt ? '✓ Decrypt' : '✗ Decrypt'}
                                </span>
                              </div>
                            </td>
                            <td>
                              <strong>{k.activeCiphertextCount}</strong>
                              {k.legacyUnversionedCount > 0 && (
                                <div style={{ fontSize: '0.75rem', color: '#ea580c' }}>
                                  +{k.legacyUnversionedCount} legacy
                                </div>
                              )}
                            </td>
                            <td>
                              {k.isEligibleForRetirement ? (
                                <span className="saas-badge saas-badge-active">Eligible</span>
                              ) : (
                                <div>
                                  <span className="saas-badge saas-badge-suspended">Blocked</span>
                                  {k.retirementBlockers && k.retirementBlockers.length > 0 && (
                                    <div style={{ fontSize: '0.75rem', color: '#b91c1c', marginTop: '0.25rem', maxWidth: '250px' }}>
                                      • {k.retirementBlockers[0]}
                                      {k.retirementBlockers.length > 1 && ` (+${k.retirementBlockers.length - 1} more)`}
                                    </div>
                                  )}
                                </div>
                              )}
                            </td>
                            <td>
                              {k.disasterRecoveryReviewed ? (
                                <span style={{ fontSize: '0.8125rem', color: '#047857' }}>
                                  ✓ Verified ({k.disasterRecoveryReviewedBy})
                                </span>
                              ) : (
                                <button
                                  className="saas-btn saas-btn-secondary"
                                  style={{ fontSize: '0.75rem', padding: '0.2rem 0.5rem' }}
                                  onClick={() => {
                                    setRecoveryModalKey(k.version);
                                    setBackupVerified(false);
                                    setRecoveryNotes('');
                                  }}
                                >
                                  Sign Off DR
                                </button>
                              )}
                            </td>
                            <td>
                              <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap' }}>
                                {/* Dry Run Button */}
                                <button
                                  className="saas-btn saas-btn-secondary"
                                  style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                                  disabled={actionLoading}
                                  onClick={() => handleDryRun(k.version)}
                                  title="Run read-only retirement audit"
                                >
                                  🔍 Dry Run
                                </button>

                                {/* Deprecate Button (only for active or decrypt-only) */}
                                {(k.state === 'ACTIVE' || k.state === 'DECRYPT_ONLY') && !k.isActiveEncryptionKey && (
                                  <button
                                    className="saas-btn saas-btn-secondary"
                                    style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', color: '#c2410c' }}
                                    disabled={actionLoading}
                                    onClick={() => handleDeprecate(k.version)}
                                    title="Disable key for any future encryption"
                                  >
                                    ⚠️ Deprecate
                                  </button>
                                )}

                                {/* Phase A: Request Retirement */}
                                {k.state === 'DEPRECATED' && (
                                  <button
                                    className="saas-btn saas-btn-primary"
                                    style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                                    disabled={actionLoading}
                                    onClick={() => handleOpenRequestRetirement(k.version)}
                                  >
                                    ⏳ Request Retirement
                                  </button>
                                )}

                                {/* Phase B: Confirm Retirement */}
                                {k.state === 'RETIREMENT_PENDING' && (
                                  <>
                                    <button
                                      className="saas-btn saas-btn-primary"
                                      style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', backgroundColor: '#dc2626' }}
                                      disabled={actionLoading}
                                      onClick={() => {
                                        setConfirmModalKey(k.version);
                                        setConfirmToken('');
                                        setConfirmComment('');
                                      }}
                                    >
                                      🛑 Confirm Retirement
                                    </button>
                                    <button
                                      className="saas-btn saas-btn-secondary"
                                      style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                                      disabled={actionLoading}
                                      onClick={() => handleCancelRetirement(k.version)}
                                    >
                                      Cancel
                                    </button>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="7" style={{ textAlign: 'center', padding: '2rem', color: '#94a3b8' }}>
                            No cryptographic keys found in provider.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}

          {/* TAB 2: Dry Run Report */}
          {activeTab === 'dryrun' && (
            <div className="saas-card">
              <div className="saas-card-header">
                <h3>Retirement Dry-Run Audit Results</h3>
                {dryRunReport && (
                  <span className={`saas-badge ${dryRunReport.isEligibleForRetirement ? 'saas-badge-active' : 'saas-badge-suspended'}`}>
                    {dryRunReport.isEligibleForRetirement ? 'ELIGIBLE FOR RETIREMENT' : 'RETIREMENT BLOCKED'}
                  </span>
                )}
              </div>
              <div className="saas-card-body">
                {dryRunReport ? (
                  <div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem', marginBottom: '1.5rem' }}>
                      <div className="saas-stat-card" style={{ padding: '0.75rem' }}>
                        <span className="saas-stat-label">Candidate Key</span>
                        <strong style={{ fontFamily: 'monospace', fontSize: '1.25rem' }}>{dryRunReport.keyVersion}</strong>
                      </div>
                      <div className="saas-stat-card" style={{ padding: '0.75rem' }}>
                        <span className="saas-stat-label">Scanned Ciphertexts</span>
                        <strong style={{ fontSize: '1.25rem' }}>{dryRunReport.totalCiphertextsScanned}</strong>
                      </div>
                      <div className="saas-stat-card" style={{ padding: '0.75rem' }}>
                        <span className="saas-stat-label">Ciphertexts on This Key</span>
                        <strong style={{ fontSize: '1.25rem', color: dryRunReport.candidateKeyCiphertextCount > 0 ? '#b91c1c' : '#047857' }}>
                          {dryRunReport.candidateKeyCiphertextCount}
                        </strong>
                      </div>
                      <div className="saas-stat-card" style={{ padding: '0.75rem' }}>
                        <span className="saas-stat-label">Round-Trip Verifications</span>
                        <strong style={{ fontSize: '1.25rem', color: '#047857' }}>
                          {dryRunReport.successfullyDecryptedCount} / {dryRunReport.totalCiphertextsScanned}
                        </strong>
                      </div>
                    </div>

                    {/* Blockers */}
                    {dryRunReport.blockers && dryRunReport.blockers.length > 0 ? (
                      <div style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '1rem', marginBottom: '1.5rem' }}>
                        <h4 style={{ color: '#991b1b', margin: '0 0 0.5rem 0' }}>⛔ Retirement Blockers Identified:</h4>
                        <ul style={{ margin: 0, paddingLeft: '1.25rem', color: '#b91c1c' }}>
                          {dryRunReport.blockers.map((b, idx) => (
                            <li key={idx}>{b}</li>
                          ))}
                        </ul>
                      </div>
                    ) : (
                      <div style={{ backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', padding: '1rem', marginBottom: '1.5rem' }}>
                        <h4 style={{ color: '#166534', margin: '0 0 0.5rem 0' }}>✓ Zero Blockers Detected</h4>
                        <p style={{ margin: 0, color: '#15803d' }}>
                          No active ciphertexts depend on key &apos;{dryRunReport.keyVersion}&apos;. Disaster recovery review is satisfied. This key is safe to advance to two-phase retirement.
                        </p>
                      </div>
                    )}

                    {/* Recommendations */}
                    {dryRunReport.recommendations && dryRunReport.recommendations.length > 0 && (
                      <div style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '1rem', marginBottom: '1.5rem' }}>
                        <h4 style={{ color: '#334155', margin: '0 0 0.5rem 0' }}>💡 Remediation Recommendations:</h4>
                        <ul style={{ margin: 0, paddingLeft: '1.25rem', color: '#475569' }}>
                          {dryRunReport.recommendations.map((r, idx) => (
                            <li key={idx}>{r}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Diagnostics Details */}
                    {dryRunReport.diagnostics && dryRunReport.diagnostics.length > 0 && (
                      <div style={{ marginTop: '1rem' }}>
                        <h4>Sanitized Dry-Run Diagnostics:</h4>
                        <div style={{ maxHeight: '200px', overflowY: 'auto', background: '#1e293b', color: '#f8fafc', padding: '1rem', borderRadius: '6px', fontFamily: 'monospace', fontSize: '0.8125rem' }}>
                          {dryRunReport.diagnostics.map((d, idx) => (
                            <div key={idx} style={{ marginBottom: '0.25rem' }}>{d}</div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div style={{ textAlign: 'center', padding: '3rem', color: '#94a3b8' }}>
                    Select a key version in the Inventory tab and click &quot;Dry Run&quot; to perform a sanitized, read-only retirement audit.
                  </div>
                )}
              </div>
            </div>
          )}

          {/* TAB 3: Audit History */}
          {activeTab === 'history' && (
            <div className="saas-card">
              <div className="saas-card-header">
                <h3>Key Lifecycle Audit Trail</h3>
                <span style={{ fontSize: '0.8125rem', color: '#64748b' }}>Latest 50 state transitions &amp; security events</span>
              </div>
              <div className="saas-table-container">
                <table className="saas-table">
                  <thead>
                    <tr>
                      <th>Event ID</th>
                      <th>Action</th>
                      <th>Key Version</th>
                      <th>Actor</th>
                      <th>Details</th>
                      <th>Timestamp</th>
                    </tr>
                  </thead>
                  <tbody>
                    {auditHistory && auditHistory.length > 0 ? (
                      auditHistory.map((evt) => (
                        <tr key={evt.auditID}>
                          <td style={{ fontFamily: 'monospace' }}>#{evt.auditID}</td>
                          <td>
                            <span className={
                              evt.action.includes('RETIRE') ? 'saas-badge saas-badge-suspended' :
                              evt.action.includes('DRY_RUN') ? 'saas-badge saas-badge-info' : 'saas-badge saas-badge-active'
                            }>
                              {evt.action}
                            </span>
                          </td>
                          <td><strong style={{ fontFamily: 'monospace' }}>{evt.keyVersion || '—'}</strong></td>
                          <td>{evt.actorUsername}</td>
                          <td style={{ color: '#475569' }}>{evt.details}</td>
                          <td style={{ color: '#64748b' }}>{new Date(evt.createdAt).toLocaleString()}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="6" style={{ textAlign: 'center', padding: '2rem', color: '#94a3b8' }}>
                          No key lifecycle audit records found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* MODAL 1: Request Retirement */}
      {requestModalKey && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: '#fff', borderRadius: '12px', padding: '2rem', width: '500px', maxWidth: '90%' }}>
            <h3 style={{ marginTop: 0 }}>Request Retirement (Phase A): {requestModalKey}</h3>
            <p style={{ color: '#64748b', fontSize: '0.875rem' }}>
              Phase A validates retirement eligibility and generates a single-use confirmation token. Key status will advance to <code>RETIREMENT_PENDING</code>.
            </p>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '0.5rem', fontSize: '0.875rem' }}>Operator Reason &amp; Comments:</label>
              <textarea
                className="saas-search-input"
                style={{ width: '100%', minHeight: '80px', boxSizing: 'border-box' }}
                placeholder="Reason for retiring this cryptographic key version..."
                value={requestComment}
                onChange={(e) => setRequestComment(e.target.value)}
              />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
              <button className="saas-btn saas-btn-secondary" onClick={() => setRequestModalKey(null)}>
                Cancel
              </button>
              <button className="saas-btn saas-btn-primary" onClick={handleSubmitRequestRetirement} disabled={actionLoading}>
                Generate Token &amp; Request
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL 2: Confirm Retirement */}
      {confirmModalKey && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: '#fff', borderRadius: '12px', padding: '2rem', width: '550px', maxWidth: '90%' }}>
            <h3 style={{ marginTop: 0, color: '#dc2626' }}>Confirm Retirement (Phase B): {confirmModalKey}</h3>
            <p style={{ color: '#64748b', fontSize: '0.875rem' }}>
              Permanent authorization. Key will enter state <code>RETIRED</code> and can never be used for encryption or decryption.
            </p>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '0.5rem', fontSize: '0.875rem' }}>Confirmation Token:</label>
              <input
                type="text"
                className="saas-search-input"
                style={{ width: '100%', fontFamily: 'monospace', boxSizing: 'border-box' }}
                placeholder="Paste confirmation token generated in Phase A..."
                value={confirmToken}
                onChange={(e) => setConfirmToken(e.target.value)}
              />
            </div>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '0.5rem', fontSize: '0.875rem' }}>Confirmation Operator Comment:</label>
              <textarea
                className="saas-search-input"
                style={{ width: '100%', minHeight: '70px', boxSizing: 'border-box' }}
                placeholder="Explicit confirmation remarks..."
                value={confirmComment}
                onChange={(e) => setConfirmComment(e.target.value)}
              />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
              <button className="saas-btn saas-btn-secondary" onClick={() => setConfirmModalKey(null)}>
                Close
              </button>
              <button className="saas-btn saas-btn-primary" style={{ backgroundColor: '#dc2626' }} onClick={handleSubmitConfirmRetirement} disabled={actionLoading}>
                Permanently Retire Key
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL 3: Disaster Recovery Review */}
      {recoveryModalKey && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: '#fff', borderRadius: '12px', padding: '2rem', width: '500px', maxWidth: '90%' }}>
            <h3 style={{ marginTop: 0 }}>Disaster Recovery Sign-Off: {recoveryModalKey}</h3>
            <p style={{ color: '#64748b', fontSize: '0.875rem' }}>
              Before retiring any cryptographic key, disaster recovery archives and backup retention requirements must be certified.
            </p>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.875rem' }}>
                <input
                  type="checkbox"
                  checked={backupVerified}
                  onChange={(e) => setBackupVerified(e.target.checked)}
                />
                <strong>Attest: Historical backups &amp; recovery artifacts have been reviewed</strong>
              </label>
            </div>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '0.5rem', fontSize: '0.875rem' }}>Review Notes &amp; Backup Details:</label>
              <textarea
                className="saas-search-input"
                style={{ width: '100%', minHeight: '80px', boxSizing: 'border-box' }}
                placeholder="Details of backup verification, snapshot retention, and recovery test..."
                value={recoveryNotes}
                onChange={(e) => setRecoveryNotes(e.target.value)}
              />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
              <button className="saas-btn saas-btn-secondary" onClick={() => setRecoveryModalKey(null)}>
                Cancel
              </button>
              <button className="saas-btn saas-btn-primary" onClick={handleSubmitRecoveryReview} disabled={actionLoading}>
                Submit Attestation
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KeyLifecycleManagement;
